/**
 * Phase 4 — Admin Utility Review Queue
 * Route: /admin/utilities
 * Endpoints:
 *   GET   /api/admin/utilities/queue
 *   GET   /api/admin/utilities/{id}
 *   PATCH /api/admin/utilities/{id}/review
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  PauseCircle, Archive, Clock, Eye, X,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LIST = [
  'all', 'submitted', 'under_review', 'changes_requested',
  'approved', 'rejected', 'suspended', 'archived', 'draft',
];

const UTILITY_TYPES = [
  '', 'discount', 'digital_resource', 'exclusive_content', 'service_credit',
  'consultation', 'event_access', 'community_access', 'product_bonus',
  'membership_benefit', 'employee_benefit', 'referral_reward',
  'partner_offer', 'physical_perk', 'other',
];

const STATUS_META = {
  draft:              { color: theme.colors.text.muted,     label: 'Draft' },
  submitted:          { color: theme.colors.status.info,    label: 'Submitted' },
  under_review:       { color: theme.colors.status.warning, label: 'Under Review' },
  changes_requested:  { color: theme.colors.status.warning, label: 'Changes Requested' },
  approved:           { color: theme.colors.status.success, label: 'Approved' },
  rejected:           { color: theme.colors.status.error,   label: 'Rejected' },
  suspended:          { color: theme.colors.status.error,   label: 'Suspended' },
  archived:           { color: theme.colors.text.muted,     label: 'Archived' },
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  padding: '8px 10px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none',
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { color: theme.colors.text.muted, label: status };
  return (
    <span data-testid={`admin-util-status-${status}`}
      style={{ padding: '3px 10px', borderRadius: 999, background: `${meta.color}1A`,
        color: meta.color, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>
      {meta.label}
    </span>
  );
}

export default function AdminUtilitiesQueue() {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', utility_type: '', search: '' });
  const [selected, setSelected] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    admin_notes: '', rejection_reason: '', requested_changes: '',
  });
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.utility_type) params.set('utility_type', filters.utility_type);
      if (filters.search) params.set('search', filters.search);
      const res = await axios.get(`${API}/admin/utilities/queue?${params.toString()}`, auth);
      setRows(res.data?.utilities || []);
      setCounts(res.data?.status_counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load queue');
    } finally { setLoading(false); }
  }, [auth, filters]);

  useEffect(() => { load(); }, [load]);

  const openReview = async (u) => {
    setSelected(u);
    setReviewForm({
      admin_notes: u.admin_notes || '',
      rejection_reason: u.rejection_reason || '',
      requested_changes: u.requested_changes || '',
    });
  };

  const doAction = async (action) => {
    if (!selected) return;
    setActing(true);
    try {
      await axios.patch(`${API}/admin/utilities/${selected.id}/review`,
        { action, ...reviewForm }, auth);
      toast.success(`Utility ${action}`);
      setSelected(null); setReviewForm({ admin_notes: '', rejection_reason: '', requested_changes: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setActing(false); }
  };

  return (
    <div data-testid="admin-utilities-page" style={{ padding: '24px 28px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldCheck size={22} color={theme.colors.primary} />
            <div style={{ fontSize: 22, fontWeight: 900, color: theme.colors.text.primary }}>Utility Review Queue</div>
          </div>
          <div style={{ color: theme.colors.text.muted, marginTop: 4, maxWidth: 720, fontSize: 13 }}>
            Review submitted utilities before they become available inside the Kickback Contract ecosystem.
          </div>
        </div>
        <button data-testid="admin-util-refresh-btn" onClick={load}
          style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
            background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Status</label>
            <div>
              <select data-testid="admin-util-status-filter" style={inputStyle}
                value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <option value="">default (submitted + under_review)</option>
                {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Type</label>
            <div>
              <select data-testid="admin-util-type-filter" style={inputStyle}
                value={filters.utility_type} onChange={e => setFilters({ ...filters, utility_type: e.target.value })}>
                <option value="">any</option>
                {UTILITY_TYPES.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Search</label>
            <div>
              <input data-testid="admin-util-search" style={{ ...inputStyle, width: '100%' }}
                value={filters.search} placeholder="name, description, provider…"
                onChange={e => setFilters({ ...filters, search: e.target.value })} />
            </div>
          </div>
        </div>

        {/* Status counts */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {Object.entries(counts).map(([k, v]) => (
            <span key={k} data-testid={`admin-count-${k}`} style={{ padding: '3px 8px', borderRadius: 999,
              background: `${(STATUS_META[k]?.color) || theme.colors.text.muted}1A`,
              color: (STATUS_META[k]?.color) || theme.colors.text.muted,
              fontSize: 11, fontWeight: 700 }}>
              {k}: {v}
            </span>
          ))}
        </div>

        {loading ? (
          <div data-testid="admin-util-loading" style={{ padding: 20, color: theme.colors.text.muted }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="admin-util-empty" style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
            No utilities match the current filter.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {rows.map(u => (
              <div key={u.id} data-testid={`admin-util-row-${u.id}`}
                style={{ padding: 12, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm,
                  display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 800 }}>{u.utility_name}</div>
                    <StatusPill status={u.status} />
                    <span style={{ fontSize: 11, color: theme.colors.text.muted }}>{u.utility_type}</span>
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 4 }}>
                    {u.utility_description}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 4 }}>
                    Provider: {u.provider_name} ({u.provider_email}) · Submitted {new Date(u.created_at).toLocaleString()}
                  </div>
                </div>
                <button data-testid={`admin-util-review-${u.id}`} onClick={() => openReview(u)}
                  style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                    background: theme.colors.primary, color: '#fff', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                  <Eye size={14} /> Review
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
          <div data-testid="admin-util-review-modal" style={{ ...cardStyle, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Review Utility</div>
              <button data-testid="admin-util-close-review" onClick={() => setSelected(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ fontWeight: 800 }}>{selected.utility_name}</div>
              <StatusPill status={selected.status} />
              <span style={{ fontSize: 11, color: theme.colors.text.muted }}>{selected.utility_type}</span>
            </div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 10 }}>
              Provider: {selected.provider_name} ({selected.provider_email})
            </div>
            <div style={{ fontSize: 13, marginBottom: 10 }}>{selected.utility_description}</div>
            <div style={{ display: 'grid', gap: 6, marginBottom: 12, fontSize: 12, color: theme.colors.text.secondary }}>
              <div><b>Free/Paid:</b> {selected.free_or_paid}</div>
              <div><b>Fulfillment:</b> {selected.fulfillment_responsibility}</div>
              {selected.fulfillment_instructions && <div><b>Instructions:</b> {selected.fulfillment_instructions}</div>}
              {selected.eligibility_rules && <div><b>Eligibility:</b> {selected.eligibility_rules}</div>}
              {selected.limits_conditions && <div><b>Limits/Conditions:</b> {selected.limits_conditions}</div>}
              {selected.expiration_rules && <div><b>Expiration:</b> {selected.expiration_rules}</div>}
              {selected.website_or_example_link && (
                <div><b>Link:</b> <a href={selected.website_or_example_link} target="_blank" rel="noreferrer">{selected.website_or_example_link}</a></div>
              )}
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Admin Notes (internal)</label>
              <textarea data-testid="admin-notes-input" style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                value={reviewForm.admin_notes}
                onChange={e => setReviewForm({ ...reviewForm, admin_notes: e.target.value })} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Requested Changes (visible to provider)</label>
              <textarea data-testid="admin-changes-input" style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                value={reviewForm.requested_changes}
                onChange={e => setReviewForm({ ...reviewForm, requested_changes: e.target.value })} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Rejection Reason (visible to provider)</label>
              <textarea data-testid="admin-reject-input" style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                value={reviewForm.rejection_reason}
                onChange={e => setReviewForm({ ...reviewForm, rejection_reason: e.target.value })} />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button data-testid="admin-action-mark-under-review" disabled={acting} onClick={() => doAction('mark_under_review')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.warning, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Clock size={14} /> Mark Under Review
              </button>
              <button data-testid="admin-action-approve" disabled={acting} onClick={() => doAction('approve')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={14} /> Approve
              </button>
              <button data-testid="admin-action-request-changes" disabled={acting} onClick={() => doAction('request_changes')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={14} /> Request Changes
              </button>
              <button data-testid="admin-action-reject" disabled={acting} onClick={() => doAction('reject')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.error, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <XCircle size={14} /> Reject
              </button>
              <button data-testid="admin-action-suspend" disabled={acting} onClick={() => doAction('suspend')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6, color: theme.colors.status.error }}>
                <PauseCircle size={14} /> Suspend
              </button>
              <button data-testid="admin-action-archive" disabled={acting} onClick={() => doAction('archive')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Archive size={14} /> Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
